import './user-cards-associations.scss';
import {MutableRefObject, useRef, useState} from 'react';
import {Box, Button, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {UserCard} from '../../backend/interfaces/user-card';
import {UserCardsRepository} from '../../repositories/user-cards';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {translations} from '../../i18n/translations';
import {PutUserCard} from '../../backend/requests/put-user-card';
import {usePaginatedTable} from '../../hooks/paginated-table';
import {PageHeader} from '../page-header/page-header';
import {PaginatedTable} from '../paginated-table/paginated-table';
import {getBrandName} from '../../utils/functions/get-product-name';
import {useAppContext} from '../../contexts/app/context';
import AddIcon from '@mui/icons-material/Add';
import {CaregiverMode} from '../../enums';
import GenericHeader from '../generic-header/generic-header';
import {useFetchAllItems} from '../../hooks/fetch-all-items';
import {Modal} from '../modal/modal';
import {FlatSelectDropdown} from '../flat-select-dropdown/flat-select-dropdown';
// Columns IDs
enum ColumnIdentifier {
  Name = 'nome',
  State = 'state',
  RemoveCard = 'remove-card',
}

/**
 * Listing of all the cards associated with a user.
 * On this screen, it's possible to remove an association.
 *
 * Only system administrators and professional caregivers that are managers or
 * administrators can associate/dissociate cards.
 * All other professional caregivers can only view the card listing.
 *
 * Permissions are given by props.
 */
export const UserCardsAssociations = (props: {
  caregiverMode: CaregiverMode.SystemAdministrator | CaregiverMode.Professional;
  canWrite: boolean;
  userId: string;
  userName: string;
  cardRowBaseLinkUrl: string;
  entityId?: string;
  associateCardUrl?: string;
  headerTitleCrumbs?: Array<{label: string; link: string}>;
}) => {
  const snackBarContext = useSnackBar();
  const appContext = useAppContext();
  const {t} = useTranslation();
  const [removeCardDialogOpened, setRemoveCardDialogOpened] = useState<boolean>(false);
  const [addCardDialogOpened, setAddCardDialogOpened] = useState<boolean>(false);
  const [confirmAssociateCardDialogOpened, setConfirmAssociateCardDialogOpened] =
    useState<boolean>(false);
  const [associateCard, setAssociateCard] = useState<UserCard | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const cardToRemoveRef: MutableRefObject<
    {id: string; isActive?: boolean; name: string} | undefined
  > = useRef();
  const {items: institutionCards, refreshItems} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      if (!props.entityId) {
        return Promise.resolve({data: [], totalCount: 0});
      }
      return UserCardsRepository.instance.getInEntityAsAffiliatedForEntity(
        props.entityId,
        limit,
        skip,
        true
      );
    },
    deps: [props.entityId],
    enabled: props.caregiverMode === CaregiverMode.Professional,
  });

  // Fetch cards
  const handleGetCards = async (limit: number, skip: number) => {
    // Cards
    let result: {totalCount: number; data: Array<UserCard>} = {totalCount: 0, data: []};

    // Load cards
    if (props.caregiverMode === CaregiverMode.Professional) {
      // Check the entity id
      if (!props.entityId) {
        // Error
        throw new Error('Empty entity id.');
      }

      result = await UserCardsRepository.instance.getForUserAsAffiliatedForEntity(
        props.userId,
        limit,
        skip,
        props.entityId
      );
    } else if (props.caregiverMode === CaregiverMode.SystemAdministrator) {
      result = await UserCardsRepository.instance.getAsSystemAdministrator(
        limit,
        skip,
        undefined,
        props.userId,
        false,
        undefined
      );
    } else {
      // Error
      throw new Error('No permissions.');
    }

    // Process data
    const processedData: PaginatedTableData<UserCard> = result.data.map((obj) => {
      // Columns
      const columns: Array<{
        columnId: string;
        value?: string;
      }> = [
        {
          columnId: ColumnIdentifier.Name,
          value: obj.name || '',
        },
        {
          columnId: ColumnIdentifier.State,
          value: obj.isActive
            ? t(translations.user_cards_associations.state_value_true)
            : t(translations.user_cards_associations.state_value_false),
        },
      ];

      // Return row values
      return {
        id: obj.id || '',
        item: obj,
        enabled: obj.isActive,
        toLink:
          props.cardRowBaseLinkUrl.charAt(props.cardRowBaseLinkUrl.length - 1) === '/'
            ? `${props.cardRowBaseLinkUrl}${obj.id}`
            : `${props.cardRowBaseLinkUrl}/${obj.id}`,
        columns: props.canWrite ? [...columns, {columnId: ColumnIdentifier.RemoveCard}] : columns,
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Ask to remove the card from the senior
  const handleAskRemoveCard = (id: string) => {
    // Get the card name
    const cardName: string | undefined = data
      .find((obj) => obj.id === id)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Get the card active state
    const cardActiveState: boolean | undefined = data.find((obj) => obj.id === id)?.enabled;

    // Set card for later
    cardToRemoveRef.current = {
      id,
      name: cardName || '',
      isActive: cardActiveState,
    };

    // Open modal
    setRemoveCardDialogOpened(true);
  };

  // Don't remove the card
  const handleDeclineRemoveCard = () => {
    // Close modal
    setRemoveCardDialogOpened(false);

    // Clear
    cardToRemoveRef.current = undefined;
  };

  // Remove card
  const handleRemoveCard = async () => {
    setIsSubmitting(true);

    try {
      // Verify
      if (!cardToRemoveRef.current) {
        // Throw error
        throw new Error('Empty user card.');
      } else if (typeof cardToRemoveRef.current.isActive !== 'boolean') {
        // Throw error
        throw new Error('Empty user card state.');
      }

      // Request body
      const requestBody: PutUserCard = {
        isActive: cardToRemoveRef.current.isActive,
        userId: null,
      };

      // Dissociate the user card
      await UserCardsRepository.instance.updateById(cardToRemoveRef.current.id || '', requestBody);

      // Refresh page
      if (data.length - 1 === 0 && currentPage > 1) {
        // Refresh to the previous page because the current page will be empty
        handleApplyFilters(currentPage - 1);
      } else {
        // Refresh the same page because we still have data on it
        handleApplyFilters();
      }

      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.user_cards_associations.remove_card_success_message, {
          identifier: cardToRemoveRef.current.name,
        }),
      });
      console.debug(`🟢 Card removed.`, {
        cardId: cardToRemoveRef.current?.id,
        userId: props.userId,
        userName: props.userName,
      });
    } catch (error) {
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_cards_associations.remove_card_error_message, {
          identifier: cardToRemoveRef.current?.name,
        }),
      });

      console.debug(`🔴 Could not remove the card.`, {
        cardId: cardToRemoveRef.current?.id,
        userId: props.userId,
        userName: props.userName,
        error: error,
      });
    } finally {
      cardToRemoveRef.current = undefined;
      setIsSubmitting(false);
      setAssociateCard(undefined);
      refreshItems();
    }
  };

  const handleAssociateCard = async () => {
    setIsSubmitting(true);
    try {
      // Verify but it's not expected
      if (!associateCard || !associateCard.id) {
        // Throw error
        throw new Error('Empty user card.');
      }

      // Request body
      const requestBody: PutUserCard = {
        isActive: associateCard.isActive,
        userId: props.userId,
      };

      // Associate user card
      await UserCardsRepository.instance.updateById(associateCard.id, requestBody);

      // Show toast
      snackBarContext.setOfficeToastNotification({
        severity: 'success',
        message: t(translations.associate_user_card_form.success.success_message, {
          name: props.userName,
        }),
      });
    } catch (error) {
      // Log
      console.debug('🔴 Could not associate the user card.', error);
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.associate_user_card_form.errors.error_message, {
          name: props.userName,
        }),
      });
    } finally {
      handleApplyFilters();
      refreshItems();
      setIsSubmitting(false);
      setAssociateCard(undefined);
      setConfirmAssociateCardDialogOpened(false);
    }
  };

  // Use the paginated table hook
  const {data, isLoading, error, pagesCount, currentPage, handlePageChange, handleApplyFilters} =
    usePaginatedTable<UserCard>({
      fetchData: handleGetCards,
    });

  const handleOpenAssociateCardConfirmation = () => {
    setAddCardDialogOpened(false);
    setConfirmAssociateCardDialogOpened(true);
  };

  const handleCloseAssociateCardDialogs = () => {
    setAddCardDialogOpened(false);
    setConfirmAssociateCardDialogOpened(false);
    setAssociateCard(undefined);
  };

  return (
    <Box display="flex" flexDirection="column" flexGrow={1}>
      {props.entityId && props.headerTitleCrumbs && (
        <GenericHeader
          titleCrumbs={props.headerTitleCrumbs ? props.headerTitleCrumbs : []}
          isLoading={false}
          leftColumn={<></>}
          rightColumn={
            <>
              <Button
                variant="inverted"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setAddCardDialogOpened(true)}
                disabled={!props.canWrite}
              >
                {t(translations.user_cards_associations.associate_card_button)}
              </Button>
            </>
          }
        />
      )}
      {props.associateCardUrl && props.caregiverMode === CaregiverMode.SystemAdministrator && (
        <PageHeader
          rightButtons={
            props.canWrite && (
              <Button
                variant="contained"
                color="primary"
                endIcon={<AddIcon />}
                component={Link}
                to={props.associateCardUrl}
                disabled={!props.canWrite}
              >
                {t(translations.user_cards_associations.associate_card_button)}
              </Button>
            )
          }
        />
      )}
      <Box
        className={`cards-listing-container${props.caregiverMode === CaregiverMode.SystemAdministrator ? '-sys' : ''}`}
      >
        <Box className="cards-listing-container-centered">
          <PaginatedTable
            emptyTableMessage={t(translations.user_cards_associations.no_cards)}
            columns={[
              {
                columnId: ColumnIdentifier.Name,
                title: t(translations.user_cards_associations.name_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.State,
                title: t(translations.user_cards_associations.state_header),
                flexGrow: 1,
              },
              {
                columnId: ColumnIdentifier.RemoveCard,
                title: t(translations.user_cards_associations.remove_card_header),
                flexGrow: 1,
                button: {
                  type: 'text-button',
                  showColumnValue: false,
                  textButton: {
                    label: t(translations.user_cards_associations.remove_card_button),
                    color: 'error',
                    handleClick: handleAskRemoveCard,
                  },
                  disabled: !props.canWrite,
                  disabledTooltip: t(
                    translations.user_cards_associations.remove_card_button_disabled_tooltip
                  ),
                },
              },
            ]}
            rows={data}
            isLoading={isLoading}
            error={error}
            pagesCount={pagesCount}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            linkableRows={props.caregiverMode !== CaregiverMode.Professional}
          />
        </Box>
      </Box>
      <Modal
        open={addCardDialogOpened}
        onClose={handleCloseAssociateCardDialogs}
        maxWidth="sm"
        fullWidth
        className="cards-add-modal"
        title={t(translations.user_cards.root.add_button)}
        preventCloseClick
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: handleCloseAssociateCardDialogs,
          },
          {
            label: t(translations.associate_user_card_form.save_button),
            type: 'nondestructive',
            onClick: handleOpenAssociateCardConfirmation,
            disabled: !associateCard,
          },
        ]}
      >
        <Box className="cards-add-modal-field">
          <FlatSelectDropdown
            label={t(translations.associate_user_card_form.card_name_input)}
            required
            value={
              associateCard
                ? {
                    key: associateCard.id || '',
                    label: associateCard.name || associateCard.tagFingerprint || '',
                  }
                : undefined
            }
            options={
              !institutionCards
                ? []
                : institutionCards.map((card) => ({
                    key: card.id || '',
                    label: card.name || card.tagFingerprint || '',
                  }))
            }
            onChange={(value) => {
              setAssociateCard(institutionCards?.find((card) => card.id === value.key));
            }}
          />
        </Box>
      </Modal>
      <Modal
        open={confirmAssociateCardDialogOpened}
        onClose={handleCloseAssociateCardDialogs}
        maxWidth="sm"
        fullWidth
        className="cards-listing-modal"
        preventCloseClick
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: handleCloseAssociateCardDialogs,
          },
          {
            label: t(translations.modal.confirm),
            type: 'nondestructive',
            onClick: handleAssociateCard,
            loading: isSubmitting,
          },
        ]}
      >
        <Typography className="title">
          {t(translations.associate_user_card_form.save_dialog_title, {
            name: props.userName,
            card: associateCard?.name || associateCard?.tagFingerprint,
          })}
        </Typography>
        <Typography className="subtitle">
          {t(translations.associate_user_card_form.save_dialog_message, {
            name: props.userName,
            brand: getBrandName(appContext.state.configuration.product),
          })}
        </Typography>
      </Modal>
      <Modal
        open={removeCardDialogOpened}
        onClose={handleDeclineRemoveCard}
        maxWidth="sm"
        fullWidth
        className="cards-listing-modal"
        preventCloseClick
        actions={[
          {
            label: t(translations.modal.cancel),
            type: 'destructive',
            onClick: handleDeclineRemoveCard,
          },
          {
            label: t(translations.modal.confirm),
            type: 'nondestructive',
            onClick: () => {
              setRemoveCardDialogOpened(false);
              handleRemoveCard();
            },
            loading: isSubmitting,
          },
        ]}
      >
        <Typography className="title">
          {t(translations.user_cards_associations.remove_card_dialog_title, {
            name: props.userName || '',
            card: cardToRemoveRef.current?.name || '',
          })}
        </Typography>
        <Typography className="subtitle">
          {t(translations.user_cards_associations.remove_card_dialog_message, {
            name: props.userName || '',
            brand: getBrandName(appContext.state.configuration.product),
          })}
        </Typography>
      </Modal>
    </Box>
  );
};
